import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import './PlatformForEveryone.css';

const personas = [
  {
    title: "Housewives",
    image: "/images/platform/housewives.jpg",
    color: "#00FF88",
    desc: "Learn stock market basics from home at your own pace. Build financial awareness through practical lessons in market fundamentals, technical analysis and risk management."
  },
  {
    title: "Students",
    image: "/images/platform/students.jpg",
    color: "#0EA5E9",
    desc: "Start learning about the stock market early. Build financial literacy, understand trading fundamentals and develop analytical skills alongside your studies."
  },
  {
    title: "Traders",
    image: "/images/platform/traders.jpg",
    color: "#F97316",
    desc: "Move beyond guesswork. Learn demand-supply zones, technical analysis and AI-assisted tools to develop a structured approach to trade planning and risk management."
  },
  {
    title: "Working Professionals",
    image: "/images/platform/professionals.jpg",
    color: "#A855F7",
    desc: "Busy schedule? Learn stock market analysis, swing trading and risk management at your own pace, without leaving your job or watching charts all day."
  },
  {
    title: "Business Owners",
    image: "/images/platform/businessmen.jpg",
    color: "#EAB308",
    desc: "Put your business mindset to work. Learn capital allocation, diversification and market risks to make more informed decisions about your surplus funds."
  }
];

const Card = ({ i, title, desc, image, color, progress, range, targetScale }) => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'start start']
  });

  const imageScale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);
  const scale = useTransform(progress, range, [1, targetScale]);

  return (
    <div 
      ref={containerRef}
      className="pfe-card-container"
      style={{
        top: `calc(115px + ${i * 26}px)`,
        zIndex: i + 1,
        '--card-index': i
      }}
    >
      <motion.div 
        className="pfe-card"
        style={{
          scale,
          borderColor: `${color}35`,
          boxShadow: `0 -10px 35px -5px ${color}15, 0 20px 50px -10px rgba(0,0,0,0.85)`
        }}
      >
        <div className="pfe-card-inner">
          <div className="pfe-content">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: color }} />
              <span className="text-[11px] font-mono tracking-widest uppercase font-bold" style={{ color }}>
                PERSONA 0{i + 1}
              </span>
            </div>
            <h3 className="pfe-title" style={{ color }}>{title}</h3>
            <p className="pfe-desc">{desc}</p>
            <div className="pfe-line" style={{ background: `linear-gradient(to right, ${color}, transparent)` }} />
          </div>
          
          <div className="pfe-image-wrapper">
            <motion.div className="pfe-image-inner" style={{ scale: imageScale }}>
              <img src={image} alt={title} className="pfe-image" loading="lazy" />
            </motion.div>
            <div className="pfe-image-overlay" />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default function PlatformForEveryone() {
  const containerRef = useRef(null);
  
  // Track scroll progress through the whole section
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end']
  });

  return (
    <section ref={containerRef} className="pfe-section">
      <div className="pfe-header">
        <div className="text-center mb-6">
          <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#00FF88] mb-2">
            DESIGNED FOR EVERY BACKGROUND
          </p>
          <h2 className="text-3xl sm:text-5xl font-bold text-white font-outfit mb-3">
            A Platform For Everyone
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#00FF88] to-[#00F0FF] mx-auto rounded-full" />
        </div>
      </div>
      
      <div className="pfe-cards-wrapper">
        {personas.map((persona, i) => {
          const targetScale = 1 - ((personas.length - i) * 0.035);
          
          return (
            <Card 
              key={persona.title} 
              i={i} 
              {...persona} 
              progress={scrollYProgress} 
              range={[i * 0.18, 1]} 
              targetScale={targetScale} 
            />
          );
        })}
      </div>
    </section>
  );
}
